/* [HOK-OWNER: Khiz | BUILT-WITH: Claude Code | PROJECT: hair-spray-page | SINCE: 2026-10-01 | FORKED-FROM: hok-mouth-spray-page.js]
   Same builder as the breath perfume page, plus a single-bottle mode: when the product has one
   variant there is nothing to choose, so the bundle size is the bottle count and the chooser stays hidden. */
(() => {
  /* hok-msp-part sections (2026-09-19) also load this file, so the same script tag can
     appear more than once on a page; only the first copy may run. */
  if (window.__hokMouthSprayPageJs) return;
  window.__hokMouthSprayPageJs = true;

  /* HOK+ (2026-09-12): membership line to add alongside the bundle so the
     drawer renders member pricing in the same round trip. Mirrors
     hokMembershipItem() in the bundle ATC snippets. */
  function hokMembershipLineItem() {
    try {
      if (sessionStorage.getItem('hok_plus_opt_out')) return null;
      if (/(?:^|;\s*)hok_member=1(?:;|$)/.test(document.cookie)) return null;
      const hok = window.HokMembership;
      if (hok && typeof hok.status === 'function' && hok.status().lastMembershipQty > 0) return null;
      const cfg = hok ? hok.config : JSON.parse(document.getElementById('HokMembershipConfig').textContent);
      if (!cfg || !cfg.enabled || !cfg.productValid || cfg.optedOut || cfg.suppressed) return null;
      return { id: parseInt(cfg.variantId, 10), quantity: 1, selling_plan: parseInt(cfg.sellingPlanId, 10), properties: { _hok_auto_added: 'true' } };
    } catch (e) { return null; }
  }
  /* Show the drawer through the HOK+ controller when present (it validates the
     section HTML and keeps enrollment in sync), else via the theme drawer. */
  function hokShowDrawer(cartDrawer, result) {
    const hok = window.HokMembership;
    const open = () => { try { cartDrawer.open?.(); } catch (e) { /* noop */ } };
    if (hok && typeof hok.ingestSections === 'function' && result && result.sections) {
      return Promise.resolve(hok.ingestSections(result.sections)).then(open, open);
    }
    try { cartDrawer.renderContents(result); } catch (drawerError) { open(); }
    return Promise.resolve();
  }
  const money = (cents, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: 'currency',
        currency: currency || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const initCarousel = (root, selectors) => {
    const rail = root.querySelector(selectors.rail);
    const cards = Array.from(root.querySelectorAll(selectors.card));
    const dots = Array.from(root.querySelectorAll(selectors.dot));
    const previous = root.querySelector(selectors.previous);
    const next = root.querySelector(selectors.next);
    if (!rail || !cards.length) return;

    let activeIndex = 0;
    let frame = 0;

    const setActive = (index) => {
      activeIndex = Math.max(0, Math.min(cards.length - 1, index));
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    const goTo = (index, behavior = 'smooth') => {
      const normalized = (index + cards.length) % cards.length;
      setActive(normalized);
      rail.scrollTo({
        left: cards[normalized].offsetLeft - rail.offsetLeft,
        behavior
      });
    };

    previous?.addEventListener('click', () => goTo(activeIndex - 1));
    next?.addEventListener('click', () => goTo(activeIndex + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
    rail.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const railCenter = rail.scrollLeft + rail.clientWidth / 2;
        const nearest = cards.reduce((best, card, index) => {
          const cardCenter = card.offsetLeft - rail.offsetLeft + card.offsetWidth / 2;
          return Math.abs(cardCenter - railCenter) < best.distance
            ? { index, distance: Math.abs(cardCenter - railCenter) }
            : best;
        }, { index: 0, distance: Infinity });
        setActive(nearest.index);
      });
    }, { passive: true });

    setActive(0);
  };

  const initResultAnimation = (root) => {
    const grid = root.querySelector('[data-results-grid]');
    const rings = Array.from(root.querySelectorAll('[data-result-ring]'));
    if (!grid || !rings.length) return;

    const paint = (progress) => {
      rings.forEach((ring) => {
        const value = Math.max(0, Math.min(100, Number(ring.dataset.resultValue || 0)));
        ring.style.setProperty('--result-angle', `${value * 3.6 * progress}deg`);
        const number = ring.querySelector('[data-result-number]');
        if (number) number.textContent = `${Math.round(value * progress)}%`;
      });
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      paint(1);
      return;
    }

    paint(0);
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      const duration = 1200;
      let startedAt = 0;
      const animate = (time) => {
        if (!startedAt) startedAt = time;
        const elapsed = Math.min(1, (time - startedAt) / duration);
        const eased = 1 - Math.pow(1 - elapsed, 3);
        paint(eased);
        if (elapsed < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }, { threshold: 0.28 });
    observer.observe(grid);
  };

  const initReviewAutoScroll = (root) => {
    const rail = root.querySelector('[data-review-auto-rail]');
    if (!rail || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let previousTime = performance.now();
    let pausedUntil = 0;
    let dragging = false;
    let dragStartX = 0;
    let dragStartScroll = 0;

    const pause = (milliseconds = 900) => { pausedUntil = performance.now() + milliseconds; };
    const tick = (time) => {
      if (!root.isConnected) return;
      const elapsed = Math.min(40, time - previousTime);
      previousTime = time;
      const loopWidth = rail.scrollWidth / 2;
      if (!dragging && time > pausedUntil && loopWidth > 0) {
        rail.scrollLeft += elapsed * 0.032;
        if (rail.scrollLeft >= loopWidth) rail.scrollLeft -= loopWidth;
      }
      requestAnimationFrame(tick);
    };

    rail.addEventListener('wheel', () => pause(), { passive: true });
    rail.addEventListener('pointerdown', (event) => {
      dragging = true;
      dragStartX = event.clientX;
      dragStartScroll = rail.scrollLeft;
      rail.classList.add('is-dragging');
      rail.setPointerCapture?.(event.pointerId);
      pause();
    });
    rail.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      rail.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
    });
    const stopDragging = (event) => {
      if (!dragging) return;
      dragging = false;
      rail.classList.remove('is-dragging');
      rail.releasePointerCapture?.(event.pointerId);
      pause();
    };
    rail.addEventListener('pointerup', stopDragging);
    rail.addEventListener('pointercancel', stopDragging);
    rail.addEventListener('pointerleave', (event) => { if (dragging) stopDragging(event); });
    requestAnimationFrame(tick);
  };

  const initFaqAccordion = (root) => {
    const items = Array.from(root.querySelectorAll('[data-faq-item]'));
    if (!items.length) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    items.forEach((item) => {
      const summary = item.querySelector('summary');
      const body = item.querySelector('[data-faq-body]');
      if (!summary || !body) return;
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (item.dataset.animating === 'true') return;
        if (reduceMotion) { item.open = !item.open; return; }
        item.dataset.animating = 'true';
        body.classList.add('is-animating');
        let done = false;
        const finish = (after) => {
          if (done) return;
          done = true;
          if (after) after();
          body.classList.remove('is-animating');
          body.style.height = '';
          item.dataset.animating = 'false';
        };
        if (item.open) {
          // Closing: animate from the current height to 0, then let the browser hide it.
          body.style.height = `${body.scrollHeight}px`;
          void body.offsetHeight;
          body.style.height = '0px';
          body.addEventListener('transitionend', () => finish(() => { item.open = false; }), { once: true });
          setTimeout(() => finish(() => { item.open = false; }), 360);
        } else {
          // Opening: reveal, then animate from 0 to the natural height.
          item.open = true;
          const target = body.scrollHeight;
          body.style.height = '0px';
          void body.offsetHeight;
          body.style.height = `${target}px`;
          body.addEventListener('transitionend', () => finish(), { once: true });
          setTimeout(() => finish(), 360);
        }
      });
    });
  };

  const init = (root) => {
    if (!root || root.dataset.hokMouthSprayPageReady === 'true') return;
    root.dataset.hokMouthSprayPageReady = 'true';

    const galleryItems = Array.from(root.querySelectorAll('[data-gallery-item]'));
    const galleryButtons = Array.from(root.querySelectorAll('[data-gallery-button]'));
    const offerButtons = Array.from(root.querySelectorAll('[data-bundle-count]'));
    const flavorRows = Array.from(root.querySelectorAll('[data-flavor-row]'));
    const selectorHeading = root.querySelector('[data-selector-heading]');
    const selectionCount = root.querySelector('[data-selection-count]');
    const progress = root.querySelector('[data-flavor-progress]');
    const flavorWord = (count) => (count === 1 ? 'flavor' : 'flavors');
    const addButton = root.querySelector('[data-add-bundle]');
    const stickyButton = root.querySelector('[data-sticky-add]');
    const status = root.querySelector('[data-product-status]');
    const selectedTotal = root.querySelector('[data-selected-total]');
    const selectedCompare = root.querySelector('[data-selected-compare]');
    const summaryTotal = root.querySelector('[data-summary-total]');
    const summaryCompare = root.querySelector('[data-summary-compare]');
    const summaryItems = root.querySelector('[data-summary-items]');
    const summaryCount = root.querySelector('[data-summary-count]');
    const giftCards = Array.from(root.querySelectorAll('[data-gift]'));
    const giftsCounter = root.querySelector('[data-gifts-counter]');
    const currency = root.dataset.currency || 'USD';
    const locale = root.dataset.locale || document.documentElement.lang || 'en';
    const cartBehavior = root.dataset.cartBehavior || 'cart';
    const rootPath = window.Shopify?.routes?.root || '/';
    const format = (cents) => money(cents, currency, locale);
    const formatWhole = (cents) => format(cents).replace(/([.,]00)(?=\D*$)/, '');

    // Subscribe & save (Loop). Same model as the body butter PDP: each bundle offer maps to a
    // delivery frequency, and every bottle is added with the Loop selling plan for that frequency.
    const subscriptionEnabled = root.dataset.subscriptionEnabled === 'true';
    const purchaseButtons = Array.from(root.querySelectorAll('[data-purchase]'));
    const purchasePanels = Array.from(root.querySelectorAll('[data-purchase-details]'));
    const purchaseLabel = root.querySelector('[data-purchase-label]');
    const frequencyNote = root.querySelector('[data-subscription-frequency]');
    const summaryFrequency = root.querySelector('[data-summary-frequency]');
    const stickyLabel = root.querySelector('[data-sticky-label]');
    let purchase = subscriptionEnabled && root.dataset.purchaseDefault !== 'once' ? 'subscribe' : 'once';
    let subscriptionDays = 30;
    const subscribing = () => purchase === 'subscribe';
    const parseDays = (label) => {
      const text = String(label || '').toLowerCase();
      let match;
      if ((match = text.match(/(\d+)\s*day/))) return Number(match[1]);
      if ((match = text.match(/(\d+)\s*week/))) return Number(match[1]) * 7;
      if ((match = text.match(/(\d+)\s*month/))) return Number(match[1]) * 30;
      if ((match = text.match(/(\d+)\s*year/))) return Number(match[1]) * 365;
      if (/\bweekly\b|every week/.test(text)) return 7;
      if (/\bmonthly\b|every month/.test(text)) return 30;
      if (/\bquarterly\b/.test(text)) return 90;
      if (/\byearly\b|\bannual/.test(text)) return 365;
      return null;
    };

    const flavors = flavorRows.map((row) => ({
      id: row.dataset.variantId,
      name: row.dataset.flavorName || '',
      emoji: row.dataset.flavorEmoji || '',
      price: Number(row.dataset.variantPrice || 0),
      mediaId: row.dataset.mediaId || '',
      available: row.dataset.available === 'true',
      plans: Array.from(row.querySelectorAll('[data-loop-allocation]')).map((node) => ({
        id: node.dataset.sellingPlan || '',
        price: Number(node.dataset.sellingPrice || 0),
        label: node.dataset.sellingPlanLabel || '',
        days: parseDays(node.dataset.sellingPlanLabel)
      })),
      row
    }));
    // Loop plan for a flavor at a frequency: exact day match first, else the closest plan.
    const planFor = (flavor, days = subscriptionDays) => {
      const plans = flavor?.plans || [];
      if (!plans.length) return null;
      const exact = plans.find((plan) => plan.days === Number(days));
      if (exact) return exact;
      const dated = plans.filter((plan) => plan.days !== null);
      if (!dated.length) return plans[0];
      return dated.reduce((best, plan) => (Math.abs(plan.days - days) < Math.abs(best.days - days) ? plan : best), dated[0]);
    };
    const unitPrice = (flavor, days = subscriptionDays) => {
      if (!subscribing()) return flavor?.price || 0;
      const plan = planFor(flavor, days);
      return plan ? plan.price : (flavor?.price || 0);
    };
    const availableFlavors = flavors.filter((flavor) => flavor.available);
    const flavorById = new Map(flavors.map((flavor) => [flavor.id, flavor]));

    // Bundle state: how many bottles of each flavor. Starts empty, like the body butter selector:
    // the shopper taps "Add +" on each flavor until the bundle is full.
    let bundleCount = 1;
    let chargedCount = 1;
    let fixedPrice = 0; // one-time bundle total in cents when the offer carries a fixed price
    const counts = new Map();
    let addOrder = []; // flavor ids in the order bottles were added, used to trim fairly when the bundle shrinks

    const basePrice = () => Number(root.dataset.basePrice || flavors[0]?.price || 0);
    // Mirror of the automatic HOK+ cart discount, applied to displayed one-time prices only
    // (subscription plan prices are already final). See member_discount_percent in the schema.
    const memberPct = Math.min(90, Math.max(0, Number(root.dataset.memberDiscount || 0)));
    // Shopify rounds the discount amount DOWN when allocating (verified 2026-09-19),
    // so subtract the floored discount instead of rounding the discounted total.
    const memberPrice = (cents) => cents - Math.floor(cents * memberPct / 100);
    const exampleFlavor = () => flavorById.get(root.dataset.defaultVariant) || availableFlavors[0] || flavors[0] || null;
    // Per-bottle subscription price for a frequency, used for the offer cards and unpicked bottles.
    const baseSubscriptionPrice = (days = subscriptionDays) => {
      const example = exampleFlavor();
      const plan = example ? planFor(example, days) : null;
      return plan ? plan.price : basePrice();
    };
    const picked = () => Array.from(counts.values()).reduce((total, quantity) => total + quantity, 0);
    const increment = (id) => { counts.set(id, (counts.get(id) || 0) + 1); addOrder.push(id); };
    const decrement = (id) => {
      const quantity = counts.get(id) || 0;
      if (quantity <= 1) counts.delete(id); else counts.set(id, quantity - 1);
      const index = addOrder.lastIndexOf(id);
      if (index >= 0) addOrder.splice(index, 1);
    };
    const fitToBundle = () => {
      // Shrinking the bundle trims the most recently added bottles; growing it leaves the shopper to add more.
      while (picked() > bundleCount && addOrder.length) decrement(addOrder[addOrder.length - 1]);
    };
    const isComplete = () => picked() === bundleCount && Array.from(counts.keys()).every((id) => flavorById.get(id)?.available);
    // Single-bottle mode (data-single-variant on the root): the one variant is picked bundleCount times
    // on every render, so the offer cards alone drive the cart and the hidden chooser never blocks the add.
    const singleVariant = root.dataset.singleVariant || '';
    const syncSingle = () => {
      if (!singleVariant) return;
      counts.clear(); addOrder = [];
      for (let i = 0; i < bundleCount; i += 1) increment(singleVariant);
    };

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      galleryItems.forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === mediaId));
      galleryButtons.forEach((button) => {
        const active = button.dataset.mediaId === mediaId;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
      });
    };

    galleryButtons.forEach((button) => button.addEventListener('click', () => showMedia(button.dataset.mediaId)));
    /* On desktop the gallery column is shorter than the buy box, which left a hole under the thumbnails. The gallery now
       follows the page down until the buy box ends. Done with a transform because the theme's layout script makes body
       the scroller and position: sticky never holds here (Khiz, 2026-10-01). */
    (() => {
      const layout = root.querySelector('.hok-mouth-spray-page__layout');
      const gallery = root.querySelector('.hok-mouth-spray-page__gallery');
      if (!layout || !gallery) return;
      let queued = false;
      const topBar = () => {
        for (const sel of ['sticky-header', '.section-header', '#shopify-section-header', '.header-wrapper']) {
          const el = document.querySelector(sel);
          if (!el) continue;
          const pos = getComputedStyle(el).position;
          if (pos === 'sticky' || pos === 'fixed') return el.offsetHeight;
        }
        return 0;
      };
      const update = () => {
        queued = false;
        const twoColumns = getComputedStyle(layout).gridTemplateColumns.trim().split(/\s+/).length >= 2;
        const room = layout.offsetHeight - gallery.offsetHeight;
        if (!twoColumns || room <= 0) { gallery.style.transform = ''; return; }
        const top = layout.getBoundingClientRect().top; /* viewport-relative, so it holds whichever element scrolls */
        const shift = Math.min(Math.max(topBar() + 20 - top, 0), room);
        gallery.style.transform = shift > 0 ? `translate3d(0, ${Math.round(shift)}px, 0)` : '';
      };
      const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
      window.addEventListener('scroll', queue, { passive: true, capture: true });
      window.addEventListener('resize', queue);
      if ('ResizeObserver' in window) new ResizeObserver(queue).observe(layout);
      update();
    })();
    /* arrows and a swipe on the picture step through the gallery; the thumbnails still jump straight to one (Khiz, 2026-10-01) */
    const stepMedia = (delta) => {
      if (!galleryItems.length) return;
      const current = Math.max(0, galleryItems.findIndex((item) => item.classList.contains('is-active')));
      const target = galleryItems[(current + delta + galleryItems.length) % galleryItems.length];
      if (target) showMedia(target.dataset.mediaId);
    };
    root.querySelectorAll('[data-gallery-prev]').forEach((button) => button.addEventListener('click', () => stepMedia(-1)));
    root.querySelectorAll('[data-gallery-next]').forEach((button) => button.addEventListener('click', () => stepMedia(1)));
    const stage = root.querySelector('.hok-mouth-spray-page__media-stage');
    if (stage) {
      let swipeX = null, swipeY = null;
      stage.addEventListener('touchstart', (event) => { const t = event.touches[0]; swipeX = t.clientX; swipeY = t.clientY; }, { passive: true });
      stage.addEventListener('touchend', (event) => {
        if (swipeX === null) return;
        const t = event.changedTouches[0]; const dx = t.clientX - swipeX, dy = t.clientY - swipeY; swipeX = swipeY = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) stepMedia(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    const render = () => {
      syncSingle();
      const size = picked();
      const remaining = bundleCount - size;
      const regularTotal = Array.from(counts.entries()).reduce((total, [id, quantity]) => total + (flavorById.get(id)?.price || basePrice()) * quantity, 0) + Math.max(0, remaining) * basePrice();
      // Subscription: Loop plan prices are already the final per-bottle prices (shown rounded to the dollar, like body butter).
      // One-time: the bundle discount (bottles charged) is applied by the matching automatic discount at checkout.
      const subscriptionTotal = Array.from(counts.entries()).reduce((total, [id, quantity]) => total + unitPrice(flavorById.get(id)) * quantity, 0) + Math.max(0, remaining) * baseSubscriptionPrice();
      const oneTimeTotal = memberPrice(fixedPrice > 0 ? fixedPrice : Math.round((regularTotal / Math.max(1, bundleCount)) * chargedCount));
      const total = subscribing() ? Math.round(subscriptionTotal) : oneTimeTotal;
      const showCompare = regularTotal > total;

      offerButtons.forEach((card) => {
        const quantity = Math.max(1, Number(card.dataset.bundleCount || 1));
        const charged = Math.min(quantity, Math.max(1, Number(card.dataset.chargedCount || quantity)));
        const days = Number(card.dataset.subscriptionDays || 30) || 30;
        const compare = basePrice() * quantity;
        const cardFixed = Number(card.dataset.fixedPrice || 0);
        const cardTotal = subscribing() ? Math.round(baseSubscriptionPrice(days) * quantity) : memberPrice(cardFixed > 0 ? cardFixed : Math.round(basePrice() * charged));
        const savings = compare - cardTotal;
        const totalNode = card.querySelector('[data-offer-total]');
        const compareNode = card.querySelector('[data-offer-compare]');
        const eachNode = card.querySelector('[data-offer-each]');
        const saveNode = card.querySelector('[data-offer-save]');
        if (totalNode) totalNode.textContent = format(cardTotal);
        if (eachNode) eachNode.textContent = format(Math.round(cardTotal / quantity));
        if (compareNode) { compareNode.textContent = format(compare); compareNode.hidden = savings <= 0; }
        if (saveNode) { saveNode.textContent = `SAVE ${formatWhole(savings)}`; saveNode.hidden = savings <= 0; }
      });

      purchaseButtons.forEach((button) => {
        const active = button.dataset.purchase === purchase;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      purchasePanels.forEach((panel) => { panel.hidden = panel.dataset.purchaseDetails !== purchase; });
      root.querySelectorAll('[data-subscription-days-label]').forEach((node) => { node.textContent = `${subscriptionDays} days`; });
      if (frequencyNote) frequencyNote.textContent = `Every ${subscriptionDays} days · Skip or cancel anytime`;
      if (purchaseLabel) {
        const text = subscribing() ? root.dataset.purchaseLabelSubscribe : root.dataset.purchaseLabelOnce;
        purchaseLabel.textContent = text || '';
        purchaseLabel.hidden = !text;
      }
      if (summaryFrequency) {
        summaryFrequency.hidden = !subscribing();
        summaryFrequency.textContent = `Delivered every ${subscriptionDays} days · Skip or cancel anytime`;
      }
      if (stickyLabel) {
        const base = stickyLabel.dataset.stickyLabelBase || '';
        stickyLabel.textContent = subscribing() ? `${base}${base ? ' · ' : ''}EVERY ${subscriptionDays} DAYS` : base;
      }
      const complete = isComplete();
      const full = size >= bundleCount;

      if (selectorHeading) selectorHeading.textContent = `Choose your ${bundleCount} ${flavorWord(bundleCount)}`;
      if (selectionCount) selectionCount.textContent = `${size} of ${bundleCount} selected`;
      if (progress) progress.style.width = `${Math.min(100, (size / Math.max(1, bundleCount)) * 100)}%`;

      flavors.forEach((flavor) => {
        const quantity = counts.get(flavor.id) || 0;
        flavor.row.classList.toggle('is-picked', quantity > 0);
        const count = flavor.row.querySelector('[data-flavor-count]');
        if (count) count.textContent = String(quantity);
        const add = flavor.row.querySelector('[data-flavor-add]');
        const stepper = flavor.row.querySelector('[data-stepper]');
        const minus = flavor.row.querySelector('[data-flavor-minus]');
        const plus = flavor.row.querySelector('[data-flavor-plus]');
        // "Add +" shows until the first bottle of a flavor is picked, then the stepper takes over.
        if (add) { add.hidden = quantity > 0; add.disabled = !flavor.available || full; }
        if (stepper) stepper.hidden = quantity === 0;
        if (minus) minus.disabled = quantity === 0;
        if (plus) plus.disabled = !flavor.available || full;
      });

      if (summaryCount) summaryCount.textContent = `${bundleCount} ${bundleCount === 1 ? 'bottle' : 'bottles'}`;
      if (summaryItems) {
        const parts = flavors
          .filter((flavor) => (counts.get(flavor.id) || 0) > 0)
          .map((flavor) => `${flavor.name}${(counts.get(flavor.id) || 0) > 1 ? ` ×${counts.get(flavor.id)}` : ''}`);
        summaryItems.textContent = parts.length ? parts.join(', ') : 'No flavors chosen yet';
        if (singleVariant) summaryItems.textContent = `${bundleCount} ${bundleCount === 1 ? 'bottle' : 'bottles'}`;
      }
      [[summaryTotal, summaryCompare], [selectedTotal, selectedCompare]].forEach(([totalNode, compareNode]) => {
        if (totalNode) totalNode.textContent = format(total);
        if (compareNode) {
          compareNode.textContent = format(regularTotal);
          compareNode.hidden = !showCompare;
        }
      });

      const missing = Math.max(1, remaining);
      /* one-variant product: there is no flavour to choose, so the only reason not to be ready is the variant being sold out (Khiz, 2026-10-01) */
      const soldOut = !!singleVariant && !flavorById.get(singleVariant)?.available;
      if (addButton) {
        addButton.disabled = !complete;
        addButton.textContent = complete
          ? (addButton.dataset.readyLabel || 'Add to cart') // label only (Jai 2026-09-13)
          : soldOut ? (addButton.dataset.soldoutLabel || 'Sold out')
          : singleVariant ? (addButton.dataset.readyLabel || 'Add to cart')
          : `${addButton.dataset.chooseLabel || 'Choose'} ${missing} more ${flavorWord(missing)}`;
      }
      if (stickyButton) {
        stickyButton.disabled = !complete;
        stickyButton.textContent = complete ? (stickyButton.dataset.readyLabel || 'Checkout') : soldOut ? 'Sold out' : singleVariant ? (stickyButton.dataset.readyLabel || 'Checkout') : `${missing} left`;
      }
    };

    const clearStatus = () => { if (status) status.textContent = ''; };

    const giftsUnlockedBy = (quantity) => giftCards.filter((card) => Number(card.dataset.giftMin || 1) <= quantity).length;
    const renderGifts = () => {
      if (!giftCards.length) return;
      giftCards.forEach((card) => {
        const min = Number(card.dataset.giftMin || 1);
        const locked = min > bundleCount;
        card.classList.toggle('is-locked', locked);
        const statusNode = card.querySelector('[data-gift-status]');
        if (statusNode) {
          statusNode.textContent = locked
            ? `Unlocks with ${min}+ ${min === 1 ? 'bottle' : 'bottles'}`
            : (statusNode.dataset.giftValue ? `${statusNode.dataset.giftValue} value · Free` : 'Included free');
        }
      });
      const unlocked = giftsUnlockedBy(bundleCount);
      if (giftsCounter) giftsCounter.textContent = unlocked === giftCards.length ? 'All unlocked' : `${unlocked} of ${giftCards.length} unlocked`;
    };

    const selectBundle = (button) => {
      bundleCount = Math.max(1, Number(button.dataset.bundleCount || 1));
      chargedCount = Math.min(bundleCount, Math.max(1, Number(button.dataset.chargedCount || bundleCount)));
      fixedPrice = Math.max(0, Number(button.dataset.fixedPrice || 0));
      subscriptionDays = Number(button.dataset.subscriptionDays || 30) || 30;
      offerButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      fitToBundle();
      clearStatus();
      render();
      renderGifts();
    };

    offerButtons.forEach((button) => button.addEventListener('click', () => selectBundle(button)));
    purchaseButtons.forEach((button) => button.addEventListener('click', () => {
      purchase = button.dataset.purchase === 'subscribe' && subscriptionEnabled ? 'subscribe' : 'once';
      clearStatus();
      render();
    }));
    flavors.forEach((flavor) => {
      const addOne = () => {
        if (!flavor.available || picked() >= bundleCount) return;
        increment(flavor.id);
        showMedia(flavor.mediaId);
        clearStatus();
        render();
      };
      flavor.row.querySelector('[data-flavor-add]')?.addEventListener('click', addOne);
      flavor.row.querySelector('[data-flavor-plus]')?.addEventListener('click', addOne);
      flavor.row.querySelector('[data-flavor-minus]')?.addEventListener('click', () => {
        if (!(counts.get(flavor.id) > 0)) return;
        decrement(flavor.id);
        clearStatus();
        render();
      });
    });

    const addBundle = async () => {
      if (!isComplete()) return;
      const items = [];
      for (const [id, quantity] of counts.entries()) {
        const flavor = flavorById.get(id);
        const item = { id: Number(id), quantity, properties: { _HOK_bundle_size: String(bundleCount) } };
        if (subscribing()) {
          const plan = planFor(flavor, subscriptionDays);
          if (!plan?.id) {
            if (status) status.textContent = `${flavor?.name || 'One flavor'} is missing the ${subscriptionDays}-day Loop selling plan. Map it to that frequency in Loop, or choose one-time purchase.`;
            return;
          }
          item.selling_plan = Number(plan.id);
          item.properties._HOK_delivery = `Every ${subscriptionDays} days`;
        }
        items.push(item);
      }

      [addButton, stickyButton].forEach((button) => { if (button) button.disabled = true; });
      if (status) status.textContent = root.dataset.addingLabel || 'Adding your bundle…';

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        // HOK+ enrollment rides along as a line item either way, so 'checkout'
        // (2026-09-19) may skip the slide cart; 'cart' keeps the drawer flow.
        const goCheckout = root.dataset.cartBehavior === 'checkout';
        const useDrawer = !goCheckout && !!(cartDrawer && typeof cartDrawer.renderContents === 'function');
        const hokItem = hokMembershipLineItem();
        if (hokItem) items.push(hokItem);
        const payload = { items };
        if (useDrawer) {
          payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          payload.sections_url = '/'; // rendering under the product URL returns a broken drawer
        }
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this bundle.');
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: result }));
        if (useDrawer) {
          await hokShowDrawer(cartDrawer, result);
          [addButton, stickyButton].forEach((button) => { if (button) button.disabled = false; });
          if (status) status.textContent = '';
          render();
          return;
        }
        window.location.assign(goCheckout ? `${rootPath}checkout` : `${rootPath}cart`);
      } catch (error) {
        if (status) status.textContent = error instanceof Error ? error.message : 'Unable to add this bundle. Please try again.';
        render();
      }
    };

    addButton?.addEventListener('click', addBundle);
    stickyButton?.addEventListener('click', addBundle);
    root.querySelector('[data-scroll-flavors]')?.addEventListener('click', () => {
      root.querySelector('[data-flavor-list]')?.scrollBy({ top: 150, behavior: 'smooth' });
    });
    const initialOffer = root.querySelector('[data-bundle-count][data-default-selected="true"]') || offerButtons[0];
    if (initialOffer) selectBundle(initialOffer);
    else { fitToBundle(); render(); renderGifts(); }

    /* Live Kaching tier prices (2026-09-19): when a deal ID is configured, read the
       deal's quantity-break prices from the Kaching app embed so price edits made in
       Kaching show here without a theme change. The offer blocks' bundle prices stay
       as the fallback when the embed or deal is unavailable. */
    const syncKachingTiers = (attempt = 0) => {
      const dealId = root.dataset.kachingDealId;
      if (!dealId) return;
      const api = window.kachingBundlesApi;
      if (!api || typeof api.fetchDeals !== 'function') {
        if (attempt < 20) setTimeout(() => syncKachingTiers(attempt + 1), 500);
        return;
      }
      Promise.resolve(api.fetchDeals()).then((deals) => {
        const deal = (Array.isArray(deals) ? deals : []).find((entry) => entry.id === dealId || entry.nanoId === dealId);
        if (!deal) return;
        // Deal prices are in the store currency; scale like the Liquid price_factor.
        const storeCents = Math.round(Number(root.dataset.singleStorePrice || 0) * 100);
        const factor = storeCents > 0 ? basePrice() / storeCents : 1;
        let changed = false;
        (deal.dealBars || []).forEach((bar) => {
          if (bar.dealBarType !== 'quantity-break' || bar.discount?.type !== 'specific') return;
          const card = offerButtons.find((button) => Number(button.dataset.bundleCount) === Number(bar.quantity));
          const cents = Math.round(Number(bar.discount.value) * 100 * factor);
          if (!card || !(cents > 0) || Number(card.dataset.fixedPrice) === cents) return;
          card.dataset.fixedPrice = String(cents);
          changed = true;
        });
        if (!changed) return;
        const selected = offerButtons.find((button) => button.classList.contains('is-selected'));
        if (selected) fixedPrice = Math.max(0, Number(selected.dataset.fixedPrice || 0));
        render();
      }).catch(() => {});
    };
    syncKachingTiers();

    initCarousel(root, {
      rail: '[data-spotlight-rail]',
      card: '[data-spotlight-card]',
      dot: '[data-spotlight-dot]',
      previous: '[data-spotlight-prev]',
      next: '[data-spotlight-next]'
    });
    initCarousel(root, {
      rail: '[data-mini-review-rail]',
      card: '[data-mini-review-card]',
      dot: '[data-mini-review-dot]',
      previous: '[data-mini-review-prev]',
      next: '[data-mini-review-next]'
    });
    initResultAnimation(root);
    initReviewAutoScroll(root);
    initFaqAccordion(root);

    root.querySelectorAll('a[href="#shop"], [data-scroll-to-shop]').forEach((control) => {
      control.addEventListener('click', (event) => {
        const shop = root.querySelector('#shop');
        if (!shop) return;
        event.preventDefault();
        shop.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
    });
  };

  /* Standalone lower-page part sections (hok-msp-part.liquid): only the shared behaviors. */
  const initPart = (root) => {
    if (root.dataset.hokMspInit) return;
    root.dataset.hokMspInit = 'true';
    initResultAnimation(root);
    initReviewAutoScroll(root);
    initFaqAccordion(root);
    root.querySelectorAll('a[href="#shop"]').forEach((control) => {
      control.addEventListener('click', (event) => {
        const shop = document.getElementById('shop');
        if (!shop) return;
        event.preventDefault();
        shop.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-hok-mouth-spray-page]').forEach(init);
    scope.querySelectorAll('[data-hok-msp-part]').forEach(initPart);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.closest?.('[data-spotlight-card], [data-review-card]');
    const rail = card?.closest?.('[data-spotlight-rail], [data-review-auto-rail]');
    if (!card || !rail) return;
    rail.scrollTo({ left: card.offsetLeft - rail.offsetLeft, behavior: 'smooth' });
  });
})();

/* cache-bust 2026-09-29 (Session B: rotate asset URLs after iPhone Safari unstyled-page report) */
